/* eslint-disable max-len */
/* eslint-disable operator-linebreak */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import { FormApi, FormState } from 'final-form';
import { useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import api from 'api/api';
import auth from 'utils/auth';
import routePaths from 'routes/routePaths';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { ACCOUNT_LENGTHS, DEFAULT_ACCOUNT_TYPE, NETWORK_REQUIRED } from 'utils/constants';
import { requiredOnly, validationCallback, validateAmount, validateAccountFAB } from 'utils/validation';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IAccount } from 'store/accounts.model';
import { IApiResponse, ITransferBeneficiary, ITransferBenLocationData } from 'utils/models';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { lookupLocalAccount, getReceipt, getTransferCharge } from './submitApi';
import {
  IFormValues,
  ITransactionField,
  ITransferResponse,
  ITransfersState,
  IExtraFields,
  sortArrays,
  FAB_BANK,
  IAccountLookup,
  IGetTransferBeneficiaries,
  ITransferCharge,
  ITransferActions,
  sortWithScheduleArrays,
} from './ToFirstAtlantic.model';
import { renderTransferFieldsFrom, renderTransferFieldsTo } from './ToFirstAtlanticFields';

const warnings = {
  MAX_BULK_TRANSFERS: 'You have reached the maximum(10) number of transfers.',
  SAME_ACCOUNT: 'The destination account should not be same as source account.',
  ACCOUNT_NOT_VERIFIED: 'The destination account was not verified.',
  ENTER_NON_INSTANT_BEN: 'You should enter the beneficiary name.',
  INSUFFICIENT_FUNDS: 'You have insuffient balance for this transaction',
};

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  amount: validateAmount(),
  destinationAccountNo: validateAccountFAB('Account Number'),
  narration: requiredOnly('Narration'),
  scheduleTransfer: yup.boolean(),
  frequency: yup.string().when('scheduleTransfer', {
    is: true,
    then: requiredOnly('Frequency'),
  }),
  startDate: yup.string().test('startDate', 'Special Date is required', (value, context) => {
    const FABValues: IFormValues = context.parent as IFormValues;
    if (FABValues?.frequency === 'specialDate') {
      return !!value;
    }
    return true;
  }),
});

interface IProps {
  accounts: IAccount[];
  handleChangeForm: () => void;
}

const INITIAL_EXTRAS: IExtraFields = {
  destinationBankName: '',
  destinationBankCode: '',
  destinationAccountName: '',
  destinationIsExistingBen: false,
  destinationCurrency: '',
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: '24px',
    margin: 0,
    paddingTop: 16,
  },
  label: {
    fontFamily: 'var(--ff-etelka-text)',
    fontSize: 14,
    lineHeight: '24px',
    '&.Mui-disabled': {
      color: '#000',
    },
  },
});

const ToFirstAtlantic = ({ accounts, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [state, setState] = useState<ITransfersState>({
    transferBeneficiaries: [],
    banks: [],
    extras: INITIAL_EXTRAS,
    lastLookedUpAccount: '',
    lastLookedUpBankCode: '',
  });

  const updateState = useCallback(
    (update: Partial<ITransfersState>) => setState((current) => ({ ...current, ...update })),
    [],
  );
  const updateExtras = useCallback(
    (update: Partial<IExtraFields>) =>
      setState((current) => ({ ...current, extras: { ...current.extras, ...update } })),
    [],
  );

  const [allBeneficiaries, setAllBeneficiaries] = useState<ITransferBeneficiary[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [shouldFindInitBen, setShouldFindInitBen] = useState(false);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [toggleSchedule, setToggleSchedule] = useState<boolean | null>(null);
  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const lookUpAccount = useCallback(
    (accountNumber: string, bankCode: string, bankName: string) => {
      updateState({ isLookingUp: true });
      updateExtras({ destinationAccountName: undefined, destinationIsExistingBen: false, beneficiaryId: undefined });

      lookupLocalAccount(accountNumber)
        .then((data: IAccountLookup) => {
          updateExtras({
            destinationAccountName: data.name,
            beneficiaryId: data.beneficiaryId,
            destinationBankName: bankName,
            destinationBankCode: bankCode,
            destinationCurrency: data.currency,
          });
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => updateState({ isLookingUp: false }));
    },
    [updateExtras, updateState],
  );

  const location = useLocation<ITransferBenLocationData>();
  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  useEffect(() => {
    setLoadingData(true);

    const fetchBeneficiaries = () =>
      api
        .get(`beneficiary/transfer?transferType=LOCAL&loginId=${auth.data?.loginId ?? ''}`)
        .then((data: IGetTransferBeneficiaries) => {
          setAllBeneficiaries(data.beneficiaries);
        })
        .catch((e) => console.error(e));

    Promise.all([fetchBeneficiaries()]).finally(() => {
      setLoadingData(false);
    });
  }, []);

  useEffect(() => {
    updateState({
      transferBeneficiaries: allBeneficiaries.filter((b) => b.transferType === 'LOCAL'),
    });
  }, [updateState, allBeneficiaries]);

  useEffect(() => {
    if (!location.state || loadingData) return;

    const { destinationAccountNo } = location.state;

    setTimeout(() => {
      mainForm.change('destinationAccountNo', destinationAccountNo);
      setShouldFindInitBen(true);
    }, 100);
  }, [loadingData, mainForm, location.state]);

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onResend = () => api.post('/customer/otp/generate');

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const validateValuesAndPrompt = useCallback(() => {
    if (!state.extras.destinationAccountName) {
      setSimpleWarning(warnings.ACCOUNT_NOT_VERIFIED);
      return false;
    }
    return true;
  }, [state.extras.destinationAccountName]);
  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const onSubmit = (values: IFormValues) => {
    if (!validateValuesAndPrompt()) {
      return;
    }
    const account = accounts.find((a) => a.accountNumber === values.sourceAccount);

    if (Number(account?.availableBalance) < Number(values.amount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg(`Transfer failed.
      Insufficient funds to complete the transaction`);
      return;
    }

    if (account?.currency !== state.extras?.destinationCurrency) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg(`Transfer failed.
      Selected accounts have different currencies.Please choose accounts with the same currency for this transaction.`);
      return;
    }

    const readyValues = {
      sourceAccount: values.sourceAccount,
      narration: values.narration,
      destinationAccountName: state.extras.destinationAccountName,
      destinationAccountNo: values.destinationAccountNo,
      transactionAmount: values.amount,
      frequency: values.frequency === 'specialDate' ? 'SPECIAL DATE' : values.frequency?.toLocaleUpperCase(),
      startDate: values.startDate || '',
    };

    const newSortArrays = values.scheduleTransfer ? sortWithScheduleArrays : sortArrays;

    setPreviewData(getTransactionsArray(readyValues, undefined, undefined, newSortArrays));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const sourceAccount = accounts.find((a) => a.accountNumber === values.sourceAccount);

          const requestBody = {
            ...state.extras,
            ...values,
            password: otp,
            passwordType: 'OTP',
            transType: 'LOCAL-TRANSFER',
            currencyCode: sourceAccount?.currency,
            sourceAccountType: sourceAccount?.accountType || DEFAULT_ACCOUNT_TYPE,
            saveBen: saveBeneficiary,
            isSchedule: values.scheduleTransfer,
          };
          delete requestBody.scheduleTransfer;

          if (requestBody.beneficiaryId === undefined) delete requestBody.beneficiaryId;
          api
            .post('transfer/local', requestBody)
            .then((response: ITransferResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              setSubmitStatus(AxiosStatus.SUCCESS);
              closeConfirmation();
              setTransactionId(response.transactionId);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer is failed.\nUnable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts, state.extras],
  );

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const findBeneficiaryName = useCallback(
    (values: IFormValues) => {
      const { destinationAccountNo, destinationBankCode } = values || {};

      if (
        !destinationAccountNo ||
        (destinationAccountNo === state.lastLookedUpAccount &&
          destinationBankCode !== undefined &&
          destinationBankCode === state.lastLookedUpBankCode)
      ) {
        return;
      }

      const beneficiary = state.transferBeneficiaries.find((ben) => ben.accountNumber === destinationAccountNo);
      const beneficiaryName = beneficiary?.name;
      let updateLastLookedUp = false;

      if (!beneficiaryName) {
        if (!ACCOUNT_LENGTHS.includes(destinationAccountNo?.length)) {
          return;
        }
        const lookBankCode = FAB_BANK.code;
        const lookBankName = FAB_BANK.name;

        lookUpAccount(destinationAccountNo, lookBankCode, lookBankName);
        updateLastLookedUp = true;
      } else if (beneficiary && beneficiaryName) {
        updateExtras({
          destinationAccountName: beneficiaryName,
          destinationBankName: FAB_BANK.name,
          destinationBankCode: FAB_BANK.code,
          destinationIsExistingBen: true,
          destinationCurrency: beneficiary.alias,
          beneficiaryId: beneficiary.instantId ? +beneficiary.instantId : undefined,
        });
        updateLastLookedUp = true;
      }

      if (updateLastLookedUp) {
        updateState({ lastLookedUpAccount: destinationAccountNo, lastLookedUpBankCode: destinationBankCode });
      }
    },
    [
      state.lastLookedUpAccount,
      state.lastLookedUpBankCode,
      state.transferBeneficiaries,
      lookUpAccount,
      updateExtras,
      updateState,
    ],
  );

  const handleSelectBank = useCallback(
    (bankCode: string) => {
      const selectedBank = state.banks.find((b) => b.code === bankCode);
      if (selectedBank) {
        updateExtras({
          destinationBankName: selectedBank.name,
          destinationBankCode: selectedBank.code,
        });
      }
    },
    [state.banks, updateExtras],
  );

  const onChangeForm = useCallback(
    ({ dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
    },
    [handleChangeForm],
  );

  const getTransactionCharge = useCallback(
    (amount) => {
      updateExtras({ charge: undefined });

      getTransferCharge(amount)
        .then((response: ITransferCharge) => updateExtras({ charge: response.charge }))
        .catch((err) => console.error(err));
    },
    [updateExtras],
  );

  const actions = useMemo<ITransferActions>(
    () => ({
      getTransactionCharge,
      findBeneficiaryName,
      handleSelectBank,
    }),
    [getTransactionCharge, findBeneficiaryName, handleSelectBank],
  );

  useEffect(() => {
    if (loadingData || !shouldFindInitBen || !mainForm) return;
    findBeneficiaryName(mainForm.getState().values);
    setShouldFindInitBen(false);
  }, [loadingData, shouldFindInitBen, mainForm, findBeneficiaryName]);

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);
  const classes = useStyles();

  const dateFilterChange = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const handleClearDateInput = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const toggleScheduleTransfer = () => setToggleSchedule((prev) => !prev);

  useEffect(() => {
    if (toggleSchedule == null) {
      return;
    }

    mainForm.change('scheduleTransfer', toggleSchedule);
    if (!toggleSchedule) {
      mainForm.change('frequency', '');
      mainForm.change('startDate', '');
    }
  }, [toggleSchedule]);

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ changeFieldValue, setFieldTouched }}
      render={({ handleSubmit, form, values, hasValidationErrors }) => {
        mainForm = mainForm || form;
        return (
          <form onSubmit={handleSubmit}>
            <FieldsLeveler label="Transfer From" dividerBottom>
              {renderTransferFieldsFrom(values, accounts, state, actions, form, submitting)}
            </FieldsLeveler>
            <FieldsLeveler label="Transfer To">
              {renderTransferFieldsTo(
                values,
                accounts,
                state,
                actions,
                form,
                submitting,
                toggleScheduleTransfer,
                toggleSchedule,
                classes,
                dateFilterChange,
                handleClearDateInput,
              )}
            </FieldsLeveler>
            <SubmitLeveler>
              <Button
                type="submit"
                disabled={
                  submitting || state.isLookingUp || !state.extras.destinationAccountName || hasValidationErrors
                }
                fullWidth
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Make Transfer'}
              </Button>
            </SubmitLeveler>
            <OTPConfirmationModal
              open={isConfirmationOpen}
              data={previewData}
              onClose={closeConfirmation}
              onResend={onResend}
              OTPPinError={OTPError}
              onSubmit={onConfirm(values)}
              submitStatus={submitStatus}
              onSuccess={handleOnSuccess}
              onRetry={onRetryConfirm}
              getReceipt={onGetReceipt}
              successText={successMsg}
              failureText={errorMsg}
              askSaveBeneficiary
              {...(state.extras.destinationIsExistingBen
                ? { disableSaveBeneficiary: true, defaultSaveBeneficiary: false }
                : {})}
            />
            <StatusModal
              open={showStatusModal}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onClose={onCloseStatusModal}
              onRetry={onCloseStatusModal}
              onRetryText="Close"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
              )}
            </StatusModal>
            <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
            <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
              {simpleWarning}
            </WarningModal>
          </form>
        );
      }}
    />
  );
};

export default ToFirstAtlantic;
